import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

export interface CurrentUser {
  id: string;
  email: string;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  createdAtUtc: string;
}

export interface PublishedAssessment {
  id: string;
  name: string;
  versionNumber: number;
  language: string;
}

export interface Campaign {
  id: string;
  organizationId: string;
  assessmentVersionId: string;
  name: string;
  description: string | null;
  status: string;
  createdAtUtc: string;
}

export interface IssuedInvitation {
  id: string;
  secret: string;
  displayCode: string;
  expiresAtUtc: string;
}

export interface CandidateInvitation {
  id: string;
  campaignName: string;
  organizationName: string;
  expiresAtUtc: string;
  consentAccepted: boolean;
}

export interface CandidateItem {
  itemId: string;
  sequence: number;
  domain: string;
  prompt: string;
  options: { code: string; text: string }[];
  safeSvg: string | null;
}

export interface CandidateProgress {
  invitationId: string;
  answeredItems: number;
  completed: boolean;
  currentItem: CandidateItem | null;
  message: string;
}

export interface ConsumerProduct {
  id: string;
  code: string;
  name: string;
  priceMinorUnits: number;
  currency: string;
}

export interface ConsumerPreview {
  reportId: string;
  answeredItems: number;
  rangeLabel: string;
  unlocked: boolean;
}

export interface ConsumerProgress {
  assessmentId: string;
  answeredItems: number;
  completed: boolean;
  currentItem: CandidateItem | null;
  preview: ConsumerPreview | null;
  message: string;
}

export interface ConsumerOrder {
  id: string;
  reportId: string;
  amountMinorUnits: number;
  currency: string;
  provider: string;
  status: string;
  checkoutUrl: string | null;
  createdAtUtc: string;
}

export interface ConsumerReportSummary {
  id: string;
  assessmentVersion: string;
  answeredItems: number;
  generatedAtUtc: string;
  unlocked: boolean;
}

export interface CreditBundle {
  id: string;
  code: string;
  name: string;
  credits: number;
  priceMinorUnits: number;
  currency: string;
}

export interface OrganizationInvoice {
  id: string;
  invoiceNumber: string;
  credits: number;
  amountMinorUnits: number;
  currency: string;
  status: string;
  paidAtUtc: string;
  refundedAtUtc: string | null;
}

export interface CampaignDashboard {
  availableCredits: number;
  reservedCredits: number;
  consumedCredits: number;
  invitationsIssued: number;
  invitationsRedeemed: number;
  invitationsRevoked: number;
  completedAssessments: number;
  completionRate: number;
}

export interface PrivacyRequestRecord {
  id: string;
  type: string;
  status: string;
  details: string | null;
  resultJson: string | null;
  submittedAtUtc: string;
  completedAtUtc: string | null;
}

export interface AdministrationDashboard {
  users: number;
  items: number;
  publishedItems: number;
  payments: number;
  organizationInvoices: number;
  pendingPrivacyRequests: number;
  jobs: number;
  failedJobs: number;
  auditRecords: number;
}

export interface AdminItem {
  id: string;
  familyCode: string;
  prompt: string;
  domain: string;
  calibrationStatus: string;
  published: boolean;
}

export interface OperationJob {
  id: string;
  jobType: string;
  status: string;
  attempts: number;
  createdAtUtc: string;
  completedAtUtc: string | null;
  lastError: string | null;
}

export interface AuditEntry {
  id: number;
  action: string;
  targetType: string;
  targetId: string;
  occurredAtUtc: string;
}

export interface PublicIqQuestion {
  id: string;
  domain: 'Abstract' | 'Numerical' | 'Verbal' | 'Spatial';
  prompt: string;
  options: string[];
  selectedOption: number | null;
  position: number;
  total: number;
}

export interface PublicIqSession {
  sessionId: string;
  language: string;
  startedAtUtc: string;
  expiresAtUtc: string;
  answeredCount: number;
  question: PublicIqQuestion;
}

export interface PublicIqQuestionReview {
  id: string;
  domain: PublicIqQuestion['domain'];
  prompt: string;
  selectedAnswer: string | null;
  correctAnswer: string;
  isCorrect: boolean;
  position: number;
  timeSpentSeconds?: number;
}

export interface PublicIqResult {
  correct: number;
  total: number;
  estimate: number;
  rangeLow: number;
  rangeHigh: number;
  band: 'very-strong' | 'strong' | 'typical' | 'developing' | 'practice-more';
  domains: { domain: PublicIqQuestion['domain']; correct: number; total: number }[];
}

export interface PublicIqCompletion extends PublicIqResult {
  questions: PublicIqQuestionReview[];
}

export interface PublicIqHistory {
  testId: string;
  candidateName: string;
  completedAtUtc: string;
  estimate: number;
  rangeLow: number;
  rangeHigh: number;
  band: PublicIqResult['band'];
}

export interface PublicIqVerification {
  testId: string;
  candidateName: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string | null;
  language: string;
  completedAtUtc: string;
  certificateIssuedAtUtc: string | null;
  result: PublicIqResult;
  status: 'verified';
}

interface AntiforgeryToken {
  token: string;
  headerName: string;
}

@Injectable({ providedIn: 'root' })
export class PlatformApi {
  private readonly http = inject(HttpClient);

  async startPublicIqTest(language: string): Promise<PublicIqSession> {
    return this.mutate<PublicIqSession>('/api/v1/public-iq-tests', { language });
  }

  async getPublicIqQuestion(sessionId: string, position: number): Promise<PublicIqSession> {
    return firstValueFrom(
      this.http.get<PublicIqSession>(`/api/v1/public-iq-tests/${sessionId}/questions/${position}`),
    );
  }

  async answerPublicIqQuestion(
    sessionId: string,
    questionId: string,
    option: number,
  ): Promise<void> {
    await this.mutate(`/api/v1/public-iq-tests/${sessionId}/answers`, {
      questionId,
      option,
    });
  }

  async changePublicIqLanguage(
    sessionId: string,
    language: string,
    position: number,
  ): Promise<PublicIqSession> {
    return this.mutate<PublicIqSession>(`/api/v1/public-iq-tests/${sessionId}/language`, {
      language,
      position,
    });
  }

  async completePublicIqTest(
    sessionId: string,
    firstName: string,
    lastName: string,
    dateOfBirth: string,
  ): Promise<PublicIqCompletion> {
    return this.mutate<PublicIqCompletion>(`/api/v1/public-iq-tests/${sessionId}/complete`, {
      firstName,
      lastName,
      dateOfBirth,
    });
  }

  async emailPublicIqResult(testId: string, email: string): Promise<void> {
    await this.mutate(`/api/v1/public-iq-tests/${testId}/email-result`, { email });
  }

  async getPublicIqVerification(testId: string): Promise<PublicIqVerification> {
    return firstValueFrom(
      this.http.get<PublicIqVerification>(`/api/v1/public-iq-tests/${testId}/verification`),
    );
  }

  async getCurrentUser(): Promise<CurrentUser> {
    return firstValueFrom(this.http.get<CurrentUser>('/api/v1/auth/me'));
  }

  async requestMagicLink(email: string, returnUrl = '/account'): Promise<void> {
    await this.mutate('/api/v1/auth/magic-link', { email, returnUrl });
  }

  async logout(): Promise<void> {
    await this.mutate('/api/v1/auth/logout', {});
  }

  async listPublicIqHistory(): Promise<PublicIqHistory[]> {
    return firstValueFrom(this.http.get<PublicIqHistory[]>('/api/v1/public-iq-tests/history'));
  }

  async recordProductEvent(name: string, context?: string): Promise<void> {
    await this.mutate('/api/v1/public-insights/events', { name, context: context ?? null });
  }

  async submitAssessmentFeedback(testId: string, rating: number, comment?: string): Promise<void> {
    await this.mutate('/api/v1/public-insights/feedback', {
      testId,
      rating,
      comment: comment ?? null,
    });
  }
  async developmentLogin(email: string): Promise<CurrentUser> {
    return this.mutate<CurrentUser>('/api/v1/auth/development-login', { email });
  }

  async listOrganizations(): Promise<Organization[]> {
    return firstValueFrom(this.http.get<Organization[]>('/api/v1/organizations'));
  }

  async createOrganization(name: string): Promise<Organization> {
    return this.mutate<Organization>('/api/v1/organizations', { name });
  }

  async listPublishedAssessments(): Promise<PublishedAssessment[]> {
    return firstValueFrom(this.http.get<PublishedAssessment[]>('/api/v1/assessments/published'));
  }

  async getCreditBalance(organizationId: string): Promise<number> {
    const response = await firstValueFrom(
      this.http.get<{ availableCredits: number }>(
        `/api/v1/organizations/${organizationId}/credits`,
      ),
    );
    return response.availableCredits;
  }

  async grantDevelopmentCredits(organizationId: string, amount: number): Promise<number> {
    const response = await this.mutate<{ availableCredits: number }>(
      `/api/v1/organizations/${organizationId}/development-credits`,
      { requestId: crypto.randomUUID(), amount },
    );
    return response.availableCredits;
  }

  async listCampaigns(organizationId: string): Promise<Campaign[]> {
    return firstValueFrom(
      this.http.get<Campaign[]>(`/api/v1/organizations/${organizationId}/campaigns`),
    );
  }

  async createCampaign(
    organizationId: string,
    assessmentVersionId: string,
    name: string,
  ): Promise<Campaign> {
    return this.mutate<Campaign>(`/api/v1/organizations/${organizationId}/campaigns`, {
      assessmentVersionId,
      name,
      description: 'Indicative assessment; human review remains required.',
    });
  }

  async activateCampaign(organizationId: string, campaignId: string): Promise<void> {
    await this.mutate<unknown>(
      `/api/v1/organizations/${organizationId}/campaigns/${campaignId}/activate`,
      {},
    );
  }

  async issueInvitation(organizationId: string, campaignId: string): Promise<IssuedInvitation> {
    return this.mutate<IssuedInvitation>(
      `/api/v1/organizations/${organizationId}/campaigns/${campaignId}/invitations`,
      { lifetimeHours: 168 },
    );
  }

  async redeemInvitation(secret: string): Promise<CandidateInvitation> {
    return this.mutate<CandidateInvitation>('/api/v1/candidate/invitations/redeem', { secret });
  }

  async acceptCandidateConsent(invitationId: string): Promise<void> {
    await this.mutate<unknown>(`/api/v1/candidate/invitations/${invitationId}/consent`, {
      consentVersion: 'candidate-consent-v1',
      privacyNoticeVersion: 'privacy-v1',
      accepted: true,
    });
  }

  async startOrResumeAssessment(invitationId: string): Promise<CandidateProgress> {
    return this.mutate<CandidateProgress>(
      `/api/v1/candidate/invitations/${invitationId}/assessment`,
      {},
    );
  }

  async submitCandidateAnswer(
    invitationId: string,
    itemId: string,
    optionCode: string,
  ): Promise<CandidateProgress> {
    return this.mutate<CandidateProgress>(`/api/v1/candidate/invitations/${invitationId}/answers`, {
      submissionId: crypto.randomUUID(),
      itemId,
      optionCode,
    });
  }

  async listConsumerProducts(): Promise<ConsumerProduct[]> {
    return firstValueFrom(this.http.get<ConsumerProduct[]>('/api/v1/consumer/products'));
  }

  async startConsumerAssessment(assessmentVersionId: string): Promise<ConsumerProgress> {
    return this.mutate<ConsumerProgress>('/api/v1/consumer/assessments', {
      assessmentVersionId,
    });
  }

  async submitConsumerAnswer(
    assessmentId: string,
    itemId: string,
    optionCode: string,
  ): Promise<ConsumerProgress> {
    return this.mutate<ConsumerProgress>(`/api/v1/consumer/assessments/${assessmentId}/answers`, {
      submissionId: crypto.randomUUID(),
      itemId,
      optionCode,
    });
  }

  async createConsumerOrder(
    reportId: string,
    productId: string,
    provider = 'development',
  ): Promise<ConsumerOrder> {
    return this.mutate<ConsumerOrder>('/api/v1/consumer/orders', {
      reportId,
      productId,
      provider,
      idempotencyKey: crypto.randomUUID(),
      immediateDeliveryConsent: true,
      termsAccepted: true,
    });
  }

  async completeDevelopmentOrder(orderId: string): Promise<void> {
    await this.mutate<unknown>(`/api/v1/consumer/orders/${orderId}/development-complete`, {});
  }

  async listConsumerReports(): Promise<ConsumerReportSummary[]> {
    return firstValueFrom(this.http.get<ConsumerReportSummary[]>('/api/v1/consumer/reports'));
  }

  async listCreditBundles(organizationId: string): Promise<CreditBundle[]> {
    return firstValueFrom(
      this.http.get<CreditBundle[]>(`/api/v1/organizations/${organizationId}/billing/products`),
    );
  }

  async purchaseDevelopmentBundle(
    organizationId: string,
    productId: string,
  ): Promise<OrganizationInvoice> {
    return this.mutate<OrganizationInvoice>(
      `/api/v1/organizations/${organizationId}/billing/development-purchases`,
      { productId, requestId: crypto.randomUUID() },
    );
  }

  async listOrganizationInvoices(organizationId: string): Promise<OrganizationInvoice[]> {
    return firstValueFrom(
      this.http.get<OrganizationInvoice[]>(
        `/api/v1/organizations/${organizationId}/billing/invoices`,
      ),
    );
  }

  async refundOrganizationInvoice(organizationId: string, invoiceId: string): Promise<void> {
    await this.mutate<unknown>(
      `/api/v1/organizations/${organizationId}/billing/invoices/${invoiceId}/refund`,
      {},
    );
  }

  async getCampaignDashboard(organizationId: string): Promise<CampaignDashboard> {
    return firstValueFrom(
      this.http.get<CampaignDashboard>(`/api/v1/organizations/${organizationId}/billing/dashboard`),
    );
  }

  async submitPrivacyRequest(
    type: 'Export' | 'Deletion' | 'Dispute',
    details?: string,
  ): Promise<PrivacyRequestRecord> {
    return this.mutate<PrivacyRequestRecord>('/api/v1/privacy/requests', {
      type,
      details: details ?? null,
    });
  }

  async listPrivacyRequests(): Promise<PrivacyRequestRecord[]> {
    return firstValueFrom(this.http.get<PrivacyRequestRecord[]>('/api/v1/privacy/requests'));
  }

  async getAdministrationDashboard(): Promise<AdministrationDashboard> {
    return firstValueFrom(
      this.http.get<AdministrationDashboard>('/api/v1/administration/dashboard'),
    );
  }

  async listAdminItems(): Promise<AdminItem[]> {
    return firstValueFrom(this.http.get<AdminItem[]>('/api/v1/administration/items'));
  }

  async importAdminDemoItem(): Promise<AdminItem> {
    return this.mutate<AdminItem>('/api/v1/administration/items/import', {
      familyCode: `admin-original-${Date.now()}`,
      familyName: 'Administrator original review item',
      domain: 'VerbalReasoning',
      language: 'en',
      prompt: 'Which word is closest in meaning to precise?',
      discrimination: 1.1,
      difficulty: 0,
      options: [
        { code: 'A', text: 'Exact', isCorrect: true },
        { code: 'B', text: 'Vague', isCorrect: false },
        { code: 'C', text: 'Distant', isCorrect: false },
      ],
    });
  }

  async publishAdminItem(itemId: string): Promise<void> {
    await this.mutate<unknown>(`/api/v1/administration/items/${itemId}/publish`, {});
  }

  async listAdminPrivacyRequests(): Promise<PrivacyRequestRecord[]> {
    return firstValueFrom(
      this.http.get<PrivacyRequestRecord[]>('/api/v1/administration/privacy-requests'),
    );
  }

  async processAdminPrivacyRequest(requestId: string): Promise<PrivacyRequestRecord> {
    return this.mutate<PrivacyRequestRecord>(
      `/api/v1/administration/privacy-requests/${requestId}/process`,
      {},
    );
  }

  async listOperationJobs(): Promise<OperationJob[]> {
    return firstValueFrom(this.http.get<OperationJob[]>('/api/v1/administration/jobs'));
  }

  async listAuditEntries(): Promise<AuditEntry[]> {
    return firstValueFrom(this.http.get<AuditEntry[]>('/api/v1/administration/audit?take=25'));
  }

  async runRetentionCleanup(): Promise<void> {
    await this.mutate<unknown>('/api/v1/administration/retention-cleanup', {
      cutoffUtc: new Date().toISOString(),
    });
  }

  private async mutate<T>(url: string, body: unknown): Promise<T> {
    const antiforgery = await firstValueFrom(
      this.http.get<AntiforgeryToken>('/api/v1/auth/antiforgery'),
    );
    const headers = new HttpHeaders().set(antiforgery.headerName, antiforgery.token);
    return firstValueFrom(this.http.post<T>(url, body, { headers }));
  }
}
